import { getObjectLabels } from '@paintscout/util/builder'
import { isExpired } from '@paintscout/util/builder/quote/expiry/is-expired'
import { updateCachedQuote, useQuoteUrlQuery } from '@paintscout/api'
import React, { useCallback, useEffect, useState } from 'react'
import {
  createStyles,
  withStyles,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputField,
  Spinner,
  IconButton
} from '@paintscout/ui'
import type { StyleClasses, DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useApolloClient } from '@apollo/react-hooks'
import CloseIcon from '@material-ui/icons/Close'
import type { QuoteDocument } from 'paintscout'
import { useQuote } from '@paintscout/react-quote'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'

const WORK_ORDER_VIEW = 'work-order'

export interface QuoteLinkDialogProps extends Omit<DialogProps, 'message' | 'title'> {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  view: 'quote' | 'work-order' | 'archived-items' | 'activity' | 'details' | 'terms'
  quote?: QuoteDocument
  onConfirm: () => void
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    dialogActions: {
      marginTop: theme.spacing(3)
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    linkSection: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    },
    linkInputContainer: {
      minWidth: 0
    },
    copyButtonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      flexShrink: 0,
      paddingLeft: theme.spacing(1)
    }
  })

function QuoteLinkDialog({
  classes,
  view,
  onConfirm,
  loading,
  quote: propsQuote = null,
  ...props
}: QuoteLinkDialogProps) {
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const { quote: hookQuote, isDirty, save: saveQuote } = useQuote()
  const quote = propsQuote || hookQuote
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const isQuoteExpired = isExpired({ quote, options, isWorkOrder: view === WORK_ORDER_VIEW })
  const [url, setUrl] = useState<string>(null)
  const [loadError, setLoadError] = useState<boolean>(false)

  const titleLabel = view === WORK_ORDER_VIEW ? objectLabels.workOrder.value : objectLabels.quote.value
  const client = useApolloClient()
  useQuoteUrlQuery({
    skip: isDirty || !quote._rev || !!url,
    variables: {
      id: quote._id,
      rev: quote._rev,
      view
    },
    onError: useCallback((error) => {
      setLoadError(true)
      if (!isQuoteExpired && quote.crewVisible && quote.clientVisible) {
        Sentry.captureException(error, { tags: { quoteId: quote._id } })
        enqueueSnackbar(`Unable to load ${titleLabel} link`, { variant: 'error' })
        onConfirm()
      }
    }, []),
    onCompleted: (data) => {
      if (data?.quoteUrl?.quote) {
        updateCachedQuote(client, data?.quoteUrl?.quote)
        setUrl(data?.quoteUrl?.url?.link as string)
      }
    }
  })

  useEffect(() => {
    if (isDirty) {
      saveQuote().catch(() => {
        onConfirm()
      })
    }
  }, [])

  const silentUrl = view !== WORK_ORDER_VIEW && ((url + '&s=1') as string)

  return (
    <Dialog {...props}>
      <DialogTitle
        loading={loading}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onConfirm} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        {titleLabel} Link
      </DialogTitle>
      <DialogContent className={classes.root}>
        <Grid container spacing={4} alignItems={'center'} direction={'row'}>
          {!isQuoteExpired && !loadError && (
            <Grid item xs={12}>
              <Alert noMargin={true} severity="info">
                Send this link to view the {titleLabel.toLowerCase()}
              </Alert>
            </Grid>
          )}
          {isQuoteExpired && (
            <Grid item xs={12}>
              <Alert noMargin={true} severity="warning">
                This {titleLabel.toLowerCase()} has expired, unable to copy link.
              </Alert>
            </Grid>
          )}
          {view === WORK_ORDER_VIEW && !quote.crewVisible && (
            <Grid item xs={12}>
              <Alert noMargin={true} severity="warning">
                This {objectLabels.workOrder.value} is hidden from crew, unable to copy link.
              </Alert>
            </Grid>
          )}
          {view !== WORK_ORDER_VIEW && !quote.clientVisible && (
            <Grid item xs={12}>
              <Alert noMargin={true} severity="warning">
                This {titleLabel.toLowerCase()} is hidden from customers, unable to copy link.
              </Alert>
            </Grid>
          )}
          {!url && !isQuoteExpired && (
            <Grid item xs={12}>
              <Spinner />
            </Grid>
          )}
          {!loadError && !isQuoteExpired && url && (
            <Grid item xs={12} className={classes.linkSection}>
              <CopyToClipboard
                text={url}
                onCopy={() =>
                  enqueueSnackbar(`${titleLabel} link has been copied to clipboard.`, { variant: 'success' })
                }
              >
                <Grid container spacing={0} justifyContent="space-between" alignItems={'flex-end'} wrap={'nowrap'}>
                  <Grid item xs className={classes.linkInputContainer}>
                    <InputField
                      label={view === WORK_ORDER_VIEW ? `${objectLabels.workOrder.value} Link` : 'View Tracking Link'}
                      sublabel={
                        view === WORK_ORDER_VIEW ? null : 'Anyone who clicks this link will trigger view tracking.'
                      }
                      value={url}
                      fullWidth={true}
                      inputProps={{
                        'data-testid': 'quote-link'
                      }}
                    />
                  </Grid>
                  <Grid item xs="auto" className={classes.copyButtonContainer}>
                    <Button variant={'text'} color={'primary'} onClick={onConfirm}>
                      Copy
                    </Button>
                  </Grid>
                </Grid>
              </CopyToClipboard>
            </Grid>
          )}
          {view !== WORK_ORDER_VIEW && !loadError && !isQuoteExpired && url && (
            <Grid item xs={12} className={classes.linkSection}>
              <CopyToClipboard
                text={silentUrl}
                onCopy={() =>
                  enqueueSnackbar(`${titleLabel} link has been copied to clipboard.`, { variant: 'success' })
                }
              >
                <Grid container spacing={0} justifyContent="space-between" alignItems={'flex-end'} wrap={'nowrap'}>
                  <Grid item xs className={classes.linkInputContainer}>
                    <InputField
                      label={'Preview Link'}
                      sublabel="This link will not trigger view tracking."
                      value={silentUrl}
                      fullWidth={true}
                      inputProps={{
                        'data-testid': 'quote-silent-link'
                      }}
                    />
                  </Grid>
                  <Grid item xs="auto" className={classes.copyButtonContainer}>
                    <Button variant={'text'} color={'primary'} onClick={onConfirm}>
                      Copy
                    </Button>
                  </Grid>
                </Grid>
              </CopyToClipboard>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions classes={{ root: classes.dialogActions }}>
        <Button onClick={onConfirm} disabled={loading}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withStyles(styles)(QuoteLinkDialog)
